import CustomAutoComplete from "@/modules/common/components/input/dropdowns/auto-complete";
import StyledTextFieldWithTitle from "@/modules/common/components/input/styled-text-field-with-title";
import { handleStringToBooleanValue } from "@/modules/common/utils";
import { PaymentTerms } from "@/modules/property-tracks/types";
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useRentalAvailability } from "@/modules/property-tracks/hooks/listings/rental-availability.hook";
import { format } from "date-fns";
import { useEffect } from "react";
import { Control, Controller, useController } from "react-hook-form";

export default function RentalListingForm({
  control,
  namePrefix,
  propertyUid,
}: {
  control: Control<any>;
  namePrefix?: string;
  propertyUid?: string;
}) {
  const fieldName = (field: string) =>
    namePrefix ? `${namePrefix}.${field}` : field;

  // A rented unit's rental start date is owned by its tenant contract, so it is read from contract
  // management and shown read-only. With no running tenancy the field stays the user's to fill.
  const { data: availability, isLoading: availabilityLoading } =
    useRentalAvailability({ propertyUid });
  const {
    field: { value: availableFromValue, onChange: onAvailableFromChange, ...availableFromRest },
    fieldState: availableFromState,
  } = useController({ control, name: fieldName("availableFrom") });
  const derivedAvailableFrom = availability?.derived
    ? availability.availableFrom
    : null;

  // Keep the submitted payload matching what the backend will store anyway.
  useEffect(() => {
    if (!derivedAvailableFrom) return;

    const derived = format(new Date(derivedAvailableFrom), "yyyy-MM-dd");
    const current = availableFromValue
      ? format(new Date(availableFromValue), "yyyy-MM-dd")
      : undefined;

    if (current !== derived) {
      onAvailableFromChange(new Date(derivedAvailableFrom));
    }
  }, [derivedAvailableFrom, availableFromValue, onAvailableFromChange]);

  const availabilityHelperText = () => {
    if (availableFromState.error) return availableFromState.error.message;
    if (availabilityLoading && propertyUid) return "Checking tenant contract…";
    if (!derivedAvailableFrom || !availability?.contractEndDate) return undefined;

    return `Set from the tenant contract, which ends ${format(
      new Date(availability.contractEndDate),
      "dd/MM/yyyy",
    )} (+${availability.offsetDays} days).`;
  };

  return (
    <Box
      sx={(t) => ({
        display: "flex",
        flexDirection: "column",
        gap: t.nawy.spacing.sm,
      })}
    >
      {/* Rental Status */}
      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.xs,
        })}
      >
        <Typography
          sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
        >
          Rental Status
        </Typography>
        <Controller
          control={control}
          name={"isRented"}
          render={({ field, fieldState }) => (
            <Box>
              <Typography
                sx={(t) => t.nawy.typography.english.variants.body.sm.regular}
              >
                Is this property rented?
              </Typography>
              <FormControl component="fieldset" error={!!fieldState.error}>
                <RadioGroup
                  {...field}
                  row
                  onChange={(e) =>
                    field.onChange(handleStringToBooleanValue(e.target.value))
                  }
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
                {fieldState.error && (
                  <FormHelperText>{fieldState.error.message}</FormHelperText>
                )}
              </FormControl>
            </Box>
          )}
        />
      </Box>
      <Divider
        sx={(t) => ({
          backgroundColor: t.nawy.colors.semantic.layer.accent,
          mt: "-10px",
        })}
      />

      {/* Financial details */}
      <Typography
        sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
      >
        Financial details
      </Typography>
      <Controller
        control={control}
        name={fieldName("rentalPrice")}
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="Rental amount"
            placeholder="Rental amount"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{
              input: {
                endAdornment: <Typography>EGP</Typography>,
              },
            }}
          />
        )}
      ></Controller>
      <Controller
        control={control}
        name={fieldName("annualIncreasePercent")}
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="Annual increase"
            placeholder="Annual increase"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{
              input: {
                endAdornment: <Typography>%</Typography>,
              },
            }}
          />
        )}
      ></Controller>
      <Divider
        sx={(t) => ({
          backgroundColor: t.nawy.colors.semantic.layer.accent,
        })}
      />
      {/* Rental terms */}
      <Typography
        sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
      >
        Rental terms
      </Typography>
      <Controller
        control={control}
        name={fieldName("showingFrom")}
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            value={
              field.value
                ? format(new Date(field.value), "yyyy-MM-dd")
                : undefined
            }
            type="date"
            title="Showing start date"
            placeholder="Showing start date"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      ></Controller>
      <StyledTextFieldWithTitle
        {...availableFromRest}
        onChange={onAvailableFromChange}
        value={
          availableFromValue
            ? format(new Date(availableFromValue), "yyyy-MM-dd")
            : undefined
        }
        type="date"
        title="Rental start date"
        placeholder="Rental start date"
        fullWidth
        size="small"
        disabled={!!derivedAvailableFrom}
        error={!!availableFromState.error}
        helperText={availabilityHelperText()}
      />

      {/* Payment terms */}
      <Controller
        control={control}
        name={fieldName("paymentTerms")}
        render={({ field, fieldState }) => (
          <CustomAutoComplete
            {...field}
            options={Object.entries(PaymentTerms).map(([key, value]) => ({
              label: value,
              value: key,
            }))}
            placeholder="Payment Terms"
            label="Payment Terms"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      ></Controller>
      {/* Minimum stay duration */}
      <Controller
        control={control}
        name={fieldName("minimumStayMonths")}
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="Minimum stay duration"
            placeholder="Minimum stay duration"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{
              input: {
                endAdornment: <Typography>months</Typography>,
              },
            }}
          />
        )}
      ></Controller>
      <Divider
        sx={(t) => ({
          backgroundColor: t.nawy.colors.semantic.layer.accent,
        })}
      />
      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.sm,
        })}
      >
        <Typography
          sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
        >
          Rental Features
        </Typography>

        {/* Broker commission */}
        <Controller
          control={control}
          name={fieldName("noBrokerCommission")}
          render={({ field, fieldState }) => (
            <Box>
              <Typography
                sx={(t) => t.nawy.typography.english.variants.body.sm.regular}
              >
                Broker commission
              </Typography>
              <FormControl component="fieldset" error={!!fieldState.error}>
                <RadioGroup
                  {...field}
                  row
                  onChange={(e) =>
                    field.onChange(handleStringToBooleanValue(e.target.value))
                  }
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
                {fieldState.error && (
                  <FormHelperText>{fieldState.error.message}</FormHelperText>
                )}
              </FormControl>
            </Box>
          )}
        />

        {/* Insurance deposit */}
        <Controller
          control={control}
          name={fieldName("insuranceDepositRequired")}
          render={({ field, fieldState }) => (
            <Box>
              <Typography
                sx={(t) => t.nawy.typography.english.variants.body.sm.regular}
              >
                Insurance deposit
              </Typography>
              <FormControl component="fieldset" error={!!fieldState.error}>
                <RadioGroup
                  {...field}
                  row
                  onChange={(e) =>
                    field.onChange(handleStringToBooleanValue(e.target.value))
                  }
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
                {fieldState.error && (
                  <FormHelperText>{fieldState.error.message}</FormHelperText>
                )}
              </FormControl>
            </Box>
          )}
        />

        {/* Furnished */}
        <Controller
          control={control}
          name={fieldName("furnished")}
          render={({ field, fieldState }) => (
            <Box>
              {" "}
              <Typography
                sx={(t) => t.nawy.typography.english.variants.body.sm.regular}
              >
                Furnished
              </Typography>
              <FormControl component="fieldset" error={!!fieldState.error}>
                <RadioGroup
                  {...field}
                  row
                  onChange={(e) =>
                    field.onChange(handleStringToBooleanValue(e.target.value))
                  }
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
                {fieldState.error && (
                  <FormHelperText>{fieldState.error.message}</FormHelperText>
                )}
              </FormControl>
            </Box>
          )}
        />
      </Box>
      <Divider
        sx={(t) => ({
          backgroundColor: t.nawy.colors.semantic.layer.accent,
        })}
      />
    </Box>
  );
}
